<template>
  <div
    v-if="(promotionsWithOffers?.length || 0) > 0"
    class="recommended-products"
  >
    <Heading
      tag="h2"
      size="md"
      class="recommended-products__heading"
      font-weight="regular"
      spaces-bottom
    >
      Beliebte Produkte
    </Heading>
    <DraggableScroller
      ref="draggableScrollbar"
      has-pagination
      pagination-label="Beliebte Produkte Paginierung"
      show-single-active-pagination
    >
      <ProductOfferTileSMContainer
        :items="promotionsWithOffers"
        tile-type="small"
        class="recommended-products__list-entry"
        heading-tag="h3"
        no-delay-animation
      />
    </DraggableScroller>
  </div>
</template>

<script lang="ts" setup>
import { defineComponent, ref } from 'vue';
import { useLazyAsyncData } from '#imports';
import { Heading } from '@i22/td-longtail-vue-components';
import ProductOfferTileSMContainer from '~/components/product-offer-tile-s-m-container.vue';
import DraggableScroller from '~/components/draggable-scroller.vue';
import {
  loadOffersForPromotions,
  type Promotion,
  type PromotionOffer,
} from '~/lib/offers-by-group';
import { getPromotionsFromGroup, type DateGroupedPromotions } from '~/lib/promotions';

defineComponent({ name: 'RecommendedProducts' });

interface Recommended extends Promotion {
  teaserTitle?: string;
  description?: string;
  showLocalTitle?: boolean;
}

const recommendedProductsGrouped: DateGroupedPromotions<Recommended>[] = [
  {
    startDate: '01.11.2025',
    endDate: '18.11.2025 23:59',
    promotions: [
      {
        articleNumber: '4250494923484', // Homematic IP Heizkörperthermostat 3er Set
      },
      {
        articleNumber: '0745883879533', // belkin SoundForm Mini Bluetooth On-Ear Kopfhörer für Kinder, USB-C
      },
      // product has no variants
      // {
      //   articleNumber: '4029164137250', // Homematic IP Starter Bundle Fußbodenheizung für 4 Räume
      // },
      {
        articleNumber: '5706751082085', // Denver SCK-5500BU Kids E-Scooter
      },
      {
        articleNumber: '4029164129545', // Homematic IP Heizkörperthermostat – basic 5er Set
      },
      {
        articleNumber: '4270002855137', // Edurino Figur Luka - Logisches Denken & Coding
      },
      {
        articleNumber: '4048945587626', // Teufel AIRY TWS 2
      },
    ],
  },
  {
    startDate: '19.11.2025',
    endDate: '31.12.2025 23:59',
    promotions: [
      {
        articleNumber: '0711719021872', // Sony PlayStation 5 Digital-Edition 825 GB - EA SPORTS FC 26 Bundle
      },
      {
        articleNumber: '4250494923484', // Homematic IP Heizkörperthermostat 3er Set
      },
      {
        articleNumber: '8720618634344', // Zens MagSafe 4-in-1 Charging station
      },
      {
        articleNumber: '0194644040017', // Soundcore Boom 2 SE
      },
      {
        articleNumber: '6953281008941', // Z1
      },
      {
        articleNumber: '4048945030085', // Rockster Cross
      },
      {
        articleNumber: '6932554447052', // Xiaomi S40 Pro Staubsaugerroboter
      },
      {
        articleNumber: '8806097252504', // Samsung 50 Crystal UHD U7079F4K Smart TV (2025)
      },
    ],
  },
];

const draggableScrollbar = ref(null);

const { data: promotionsWithOffers } = useLazyAsyncData<PromotionOffer['offerBySlugs']>(
  'recommended-products',
  async () => {
    const data = await loadOffersForPromotions(
      getPromotionsFromGroup(recommendedProductsGrouped),
      false,
      false
    );

    setTimeout(() => {
      // Update scroller for new pages
      draggableScrollbar.value?.updateScroller();
    }, 1);

    return data;
  }
);
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.recommended-products {
  &__heading {
    padding-inline: $grid-base;
  }

  :deep(.draggable-scroller__container) {
    list-style-type: none;
    margin-bottom: $grid-base;

    @include mq(false, $mq-sm - 1) {
      scroll-snap-type: x mandatory;
    }
  }

  :deep(.grid-container) {
    max-width: none;
    width: auto;
    padding: 0;
  }

  :deep(.pot-sm-container__row) {
    flex-wrap: nowrap;
    margin-inline: 0;
  }

  :deep(.pot-sm-container__column) {
    padding-inline: $grid-base;
    flex: 0 0 100%;

    @include mq($mq-sm) {
      flex-basis: 50%;
    }

    @include mq($mq-md) {
      flex-basis: 33.3333%;
    }

    @include mq($mq-lg) {
      flex-basis: 25%;
    }

    @include mq(false, $mq-sm - 1) {
      scroll-snap-align: start;
    }
  }

  &__hover-image {
    transition: transform 0.3s;
    transform: scale(1.05);
  }

  :deep(.product-offer-tile) {
    .product-offer-tile {
      &__product-image-container {
        justify-content: center;
        img {
          width: 100%;
          aspect-ratio: 1 / 1;
        }
      }

      &__product-image-link {
        width: 100%;

        > a {
          display: block;
        }
      }

      &__image {
        img {
          width: 100%;
          aspect-ratio: 1 / 1;
        }
      }
    }
  }
}
</style>
